import { useEffect, useMemo, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  IdeaAssessment,
  UseAssessmentEditorReturn,
} from "components/ideas/types";
import type {
  IdeaCustomFieldIdea,
  IdeaWithIdeaTemplate,
} from "components/lib/Ideas/IdeaPreview/types";

import { reducePath } from "./useIdeaEditor";

type AssessmentRound = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
>[number];

export const useAssessmentEditor = (
  idea: IdeaWithIdeaTemplate | IdeaCustomFieldIdea | null,
): UseAssessmentEditorReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const ideaId = idea?._id;
  const editTimeout = useRef<number | null>(null);
  const { t } = useTranslation();
  const [params, setSearchParams] = useSearchParams();

  const user = useAppSelector((state) => state.user);

  const assessmentRounds = useMemo(
    () => idea?.ownerIdeaTemplate?.assessments?.rounds || [],
    [idea?.ownerIdeaTemplate?.assessments?.rounds],
  );
  const paramsRoundId = params.get("assessmentRound") || "";

  useEffect(() => {
    if (!idea?.ownerIdeaTemplate || !paramsRoundId) {
      return;
    }
    const roundExists = assessmentRounds.some(
      (round: AssessmentRound) => round.id === paramsRoundId,
    );
    if (!roundExists) {
      setSearchParams((prevParams) => {
        prevParams.delete("assessmentRound");
        return prevParams;
      });
    }
  }, [
    assessmentRounds,
    idea?.ownerIdeaTemplate,
    paramsRoundId,
    setSearchParams,
  ]);

  const selectedAssessmentRoundId =
    paramsRoundId || assessmentRounds?.[0]?.id || "legacy";

  const [isAssessor, roundsToAssess] = useMemo(() => {
    let isAssessor = false;
    const roundsToAssess: Set<string> = new Set();
    idea?.assessments?.forEach((assessment: IdeaAssessment) => {
      if (assessment.user === user?._id) {
        isAssessor = true;
        roundsToAssess.add(assessment.assessmentRoundId || "legacy");
      }
    });
    return [isAssessor, Array.from(roundsToAssess)];
  }, [idea, user]);

  const isAssessingSelectedRound = useMemo(() => {
    return roundsToAssess.includes(selectedAssessmentRoundId);
  }, [roundsToAssess, selectedAssessmentRoundId]);

  const assessmentQuery = useQuery(
    initUseQuery(
      api.ideas.getAssessment,
      [ideaId!, selectedAssessmentRoundId],
      {
        isEnabled: !!ideaId && isAssessor,
        initialData: idea?.assessments?.find(
          (assessment: IdeaAssessment) =>
            (assessment.assessmentRoundId || "legacy") ===
              selectedAssessmentRoundId && user?._id === assessment.user,
        ),
      },
    ),
  );

  const setAssessmentRound = (newRoundId: string) => {
    setSearchParams((prevParams) => {
      prevParams.set("assessmentRound", newRoundId);
      return prevParams;
    });
  };

  const clearAssessmentRound = () => {
    setSearchParams((prevParams) => {
      prevParams.delete("assessmentRound");
      return prevParams;
    });
  };

  const saveAssessment = (updatedAssessment: IdeaAssessment) => {
    setIsSaving(true);
    api.ideas.updateAssessment(
      ideaId!,
      selectedAssessmentRoundId,
      // OpenAPI PUT body for this route is incorrectly spec'd; runtime payload is IdeaAssessment.
      updatedAssessment as never,
      (data) => {
        setIsSaving(false);
        api.setQueryData(
          api.ideas.getAssessment,
          [ideaId!, selectedAssessmentRoundId],
          data.assessment,
        );
        const challengeId = idea?.challenge;
        if (challengeId) {
          api.invalidateQueries(api.challenges.getAssignedAssessments, [
            challengeId,
          ]);
        }
      },
      (err) => {
        toast.error(err.message);
        setIsSaving(false);
      },
    );
  };

  /*
    Path is as follows: ['assessment_object', 'field_id', 'property']
  */
  const updateAssessment = (
    update: unknown,
    path: string[],
    suppressSave = false,
  ) => {
    const prevAssessment = assessmentQuery?.data || {};
    const updatedAssessment = reducePath(
      prevAssessment as Record<string, unknown>,
      update as Partial<Record<string, unknown>>,
      path,
    ) as IdeaAssessment;

    api.setQueryData(
      api.ideas.getAssessment,
      [ideaId!, selectedAssessmentRoundId],
      updatedAssessment,
    );
    const challengeId = idea?.challenge;
    if (challengeId) {
      api.setQueryData(
        api.challenges.getAssignedAssessments,
        [challengeId],
        (prev) => {
          const newIdeas = (prev?.ideas || []).map((prevIdea) => {
            if (prevIdea._id === ideaId) {
              return {
                ...prevIdea,
                assessments: prevIdea.assessments?.map((oldAssessment) => {
                  if (
                    oldAssessment.user === updatedAssessment.user &&
                    (oldAssessment.assessmentRoundId || "legacy") ===
                      (selectedAssessmentRoundId || "legacy")
                  ) {
                    return updatedAssessment;
                  }
                  return oldAssessment;
                }),
              };
            }
            return prevIdea;
          });
          return { ...prev, ideas: newIdeas };
        },
      );
    }

    if (editTimeout.current) {
      clearTimeout(editTimeout.current);
      editTimeout.current = null;
    }
    editTimeout.current = setTimeout(() => {
      // Second item in the path will be the field id, if saving
      if (!suppressSave) saveAssessment(updatedAssessment);
    }, 400);
  };

  const submitAssessment = () => {
    let scoredAll = true;

    const prompt = assessmentQuery?.data?.conflictOfInterest
      ?.hasConflictOfInterest ? (
      <div>
        Once submitted you will not be able to change your assessment of this{" "}
        {t("generic.idea")}.
        <br />
        <br />
        You have declared a conflict of interest. When you submit this
        assessment, no assessment scores will be submitted and the{" "}
        {t("generic.challenge")} managers will be notified. Are you sure you
        want to proceed?
      </div>
    ) : (
      <div>
        Once submitted you will not be able to change your assessment of this{" "}
        {t("generic.idea")}. You have given the following scores:
        <ul>
          {idea?.ownerIdeaTemplate?.body?.flatMap((section) => {
            if (
              section.type !== "assessment" ||
              (section.assessmentRoundId || "legacy") !==
                (selectedAssessmentRoundId || "legacy")
            ) {
              return [];
            }

            return section.fields
              .filter((field) => field.type === "assessment")
              .map((field) => {
                const fieldScore =
                  assessmentQuery.data?.assessment?.[field.id]?.score;
                // If the assessment field has not been scored at all it is undefined - only in this case we reject submission
                if (fieldScore === undefined) {
                  scoredAll = false;
                }
                return (
                  <li key={field.title}>
                    {field.title}: {fieldScore ?? 0} /{" "}
                    {Array.isArray(field.options)
                      ? 0
                      : field.options?.availableScore || 0}
                  </li>
                );
              });
          })}
        </ul>
        If you are happy with these scores, click submit now. If you would like
        to change anything, please cancel and amend.
      </div>
    );

    if (!scoredAll) {
      toast.error("You must score all fields before submitting");
      return;
    }

    util
      .confirm("Ready to submit your assessment?", prompt)
      .then(() => {
        setIsSaving(true);
        const loadingId = toast.loading("Submitting assessment...");

        api.ideas.submitAssessment(
          ideaId!,
          selectedAssessmentRoundId,
          ({ assessment: newAssessment }) => {
            updateAssessment(newAssessment.isSubmitted, ["isSubmitted"], true);
            updateAssessment(newAssessment.submittedAt, ["submittedAt"], true);
            const challengeId = idea?.challenge;
            if (challengeId) {
              api.invalidateQueries(api.challenges.getAssignedAssessments, [
                challengeId,
              ]);
              api.invalidateQueries(api.challenges.getAssessmentDashboard, [
                challengeId,
              ]);
            }
            api.invalidateQueries(api.ideas.getAssessment, [
              ideaId!,
              selectedAssessmentRoundId,
            ]);
            toast.success("Assessment submitted", { id: loadingId });
            setIsSaving(false);
          },
          (err) => {
            toast.error(`Unable to submit your assessment: ${err.message}`, {
              id: loadingId,
            });
            setIsSaving(false);
          },
        );
      })
      .catch(() => {});
  };

  return {
    isAssessor,
    isAssessingSelectedRound,
    roundsToAssess,
    selectedAssessmentRoundId,
    urlAssessmentRoundId: paramsRoundId,
    isAssessmentLoading: assessmentQuery.isLoading,
    assessmentState: assessmentQuery.data,
    assessmentIsSubmissable: util.assessmentIsSubmissable(
      assessmentQuery.data,
      idea?.ownerIdeaTemplate,
    ),
    updateAssessment,
    submitAssessment,
    isSavingAssessment: isSaving,
    setAssessmentRound,
    clearAssessmentRound,
  };
};
